import { IS_BROWSER, APPConfig } from 'config/appConfig'
import { convertToBoolean } from 'src/utils/commonUtils'

/**
 * Converts a locale code object with "lang" and "country" properties
 * into different string formats based on the "type" parameter.
 *
 * @param {Object} localeCode - Object with "lang" and "country" properties
 * @param {string} [type="queryParam"] - Format to convert to.
 * "pathParam", "queryParam", "cookie", or "all"
 * @returns {string|Object} Locale code in the requested format,
 * or an object with all formats if type is "all"
 */
function convertLocaleCode(localeCode, type = 'queryParam') {
  const { lang = '', country = '' } = localeCode

  let localModels = {
    pathParam: `${lang}/${country}`.toLowerCase(),
    queryParam: `${lang}_${country.toUpperCase()}`,
    cookie: `${lang}-${country.toUpperCase()}`,
  }

  return type === 'all' ? localModels : localModels[type]
}

/**
 * Parses the locale code from a URL based on the provided options.
 *
 * Checks the pathname for a locale code in pathParam format,
 * or the searchParams for a locale code in queryParam format.
 *
 * Falls back to the provided defaultLocale if no locale code is found.
 *
 * Returns the locale code in queryParam format e.g. "en_US".
 */
function getLocaleCodeFromUrl(options) {
  const {
    url,
    locationProps,
    at = 'pathParam',
    defaultLocale = '',
    isReverseType = true,
  } = options || {}
  /**
   * @note
   * if any new locale required to be supported then
   * we need to add in this list otherwise fails
   */
  const existingLocales = [
    'cs_CZ',
    'da_DK',
    'de_AT',
    'de_BE',
    'de_CH',
    'de_DE',
    'de_LU',
    'en_AT',
    'en_AU',
    'en_BE',
    'en_BN',
    'en_CA',
    'en_CH',
    'en_CZ',
    'en_DE',
    'en_DK',
    'en_ES',
    'en_FI',
    'en_FR',
    'en_GB',
    'en_HU',
    'en_ID',
    'en_IE',
    'en_IL',
    'en_IN',
    'en_IS',
    'en_IT',
    'en_LU',
    'en_MY',
    'en_NL',
    'en_NO',
    'en_NZ',
    'en_PH',
    'en_PL',
    'en_PT',
    'en_RO',
    'en_SE',
    'en_SG',
    'en_SK',
    'en_TH',
    'en_UA',
    'en_US',
    'en_VN',
    'en_ZA',
    'es_AR',
    'es_CL',
    'es_CO',
    'es_ES',
    'es_MX',
    'es_PE',
    'es_US',
    'fi_FI',
    'fr_BE',
    'fr_CA',
    'fr_CH',
    'fr_FR',
    'fr_LU',
    'fr_NC',
    'fr_PF',
    'hu_HU',
    'id_ID',
    'it_CH',
    'it_IT',
    'iw_IL',
    'ja_JP',
    'ja_US',
    'ko_JP',
    'ko_KR',
    'en_JP',
    'es_JP',
    'zh_JP',
    'zs_JP',
    'ms_MY',
    'nl_BE',
    'nl_NL',
    'no_NO',
    'pl_PL',
    'pt_PT',
    'ro_RO',
    'ru_UA',
    'sk_SK',
    'sv_FI',
    'sv_SE',
    'th_TH',
    'uk_UA',
    'vi_US',
    'vi_VN',
    'zh_CA',
    'zh_CN',
    'zh_MY',
    'zh_US',
  ]

  let tempUrl = url

  let localeAsArr = defaultLocale?.split('_')

  if (locationProps?.staticContext) {
    tempUrl = locationProps?.staticContext?.originalUrl
  } else if (IS_BROWSER) {
    tempUrl = location.href
  }

  if (tempUrl) {
    // adding dummy domain since express originalUrl will not have request domain
    if (!tempUrl?.startsWith('https://')) {
      tempUrl = `https://domain.com${tempUrl}`
    }
    tempUrl = new URL(tempUrl)

    if (at === 'pathParam') {
      const pathParams = tempUrl.pathname
      if (pathParams) {
        let pathList = pathParams?.split('/')?.filter(Boolean)
        if (pathList.length > 1) {
          localeAsArr = pathList
        }
      }
    } else if (at === 'queryParam') {
      const searchParams = tempUrl.searchParams
      const urlLocale = searchParams?.get('locale')
      if (urlLocale) {
        localeAsArr = urlLocale?.split('_')
      }
    }
  }

  if (localeAsArr.length > 2) {
    /**
     * @note
     * when the pathParam comes more than 3
     * we need to slice two
     */
    localeAsArr = localeAsArr.slice(0, 2)
  }

  if (isReverseType === true) {
    localeAsArr.reverse()
  }

  /**
   * @todo
   * !!! beware, when the URL doesn't have the locale params
   * !!! it will fail to fetch the locale and storeId
   * !!! example: https://domain.com/login/callback
   */

  const localPattern = `${localeAsArr[0]}_${localeAsArr[1]?.toUpperCase()}`

  if (existingLocales.includes(localPattern) === false) {
    localeAsArr = defaultLocale.split('_')
  }
  const country = localeAsArr[0]
  const lang = localeAsArr[1]?.toUpperCase?.()

  if (country && lang) {
    return `${country}_${lang}`
  }

  return ''
}

function displayLastNameFirst() {
  return convertToBoolean(APPConfig?.getAppConfig()?.displayLastNameFirst)
}

function isLATAM() {
  const locale = getLocaleCodeFromUrl()
  const LATAMLocales = ['es_AR', 'es_CL', 'es_CO', 'es_ES', 'es_MX', 'es_PE']
  return LATAMLocales.includes(locale) || false
}

function isPAC() {
  const locale = getLocaleCodeFromUrl()
  const PACLocales = ['en_AU', 'en_NZ', 'fr_PF', 'fr_NC']
  return PACLocales.includes(locale)
}

function isEurope() {
  const locale = getLocaleCodeFromUrl()
  const EULocales = [
    'cs_CZ',
    'da_DK',
    'de_AT',
    'de_BE',
    'de_CH',
    'de_DE',
    'de_LU',
    'en_AT',
    'en_BE',
    'en_CH',
    'en_CZ',
    'en_DE',
    'en_DK',
    'en_ES',
    'en_FI',
    'en_FR',
    'en_GB',
    'en_HU',
    'en_IE',
    'en_IS',
    'en_IT',
    'en_LU',
    'en_NL',
    'en_NO',
    'en_PL',
    'en_PT',
    'en_RO',
    'en_SE',
    'en_SK',
    'es_ES',
    'fi_FI',
    'fr_BE',
    'fr_CH',
    'fr_FR',
    'fr_LU',
    'hu_HU',
    'it_CH',
    'it_IT',
    'nl_BE',
    'nl_NL',
    'no_NO',
    'pl_PL',
    'pt_PT',
    'ro_RO',
    'sk_SK',
    'sv_FI',
    'sv_SE',
    'en_ZA',
  ]
  return EULocales.includes(locale) || false
}

function getUrlWithoutLocale(options) {
  const { req, useStrictLocaleRemoval = false } = options
  const appConfigFromJSON = APPConfig.getConfigFromJSON()
  const curLocaleFromUrl = getLocaleCodeFromUrl({
    url: req.originalUrl,
    at: 'pathParam',
    defaultLocale: appConfigFromJSON?.defaultLocale,
    isReverseType: true,
  })
  const localeInUrl = curLocaleFromUrl
    ?.split('_')
    ?.reverse()
    ?.join('/')
    ?.toLowerCase()

  let urlWithoutLocale

  if (useStrictLocaleRemoval) {
    // Remove locale only if it appears at the start of the path (e.g., /us/en/...)
    // Use string methods instead of RegExp to avoid ReDoS vulnerability
    const localePrefix = `/${localeInUrl}`
    const originalUrl = req.originalUrl || ''

    if (originalUrl.toLowerCase().startsWith(localePrefix.toLowerCase())) {
      const afterLocale = originalUrl.substring(localePrefix.length)
      // Only remove if followed by /, query string, or end of string
      if (
        afterLocale === '' ||
        afterLocale.startsWith('/') ||
        afterLocale.startsWith('?')
      ) {
        urlWithoutLocale = afterLocale
      } else {
        urlWithoutLocale = originalUrl
      }
    } else {
      urlWithoutLocale = originalUrl
    }
  } else {
    // Existing behavior: remove first occurrence anywhere
    urlWithoutLocale = req.originalUrl?.replace(`/${localeInUrl}`, '')
  }

  urlWithoutLocale = urlWithoutLocale ? urlWithoutLocale : '/'
  urlWithoutLocale = urlWithoutLocale?.split('?')?.[0]
  return urlWithoutLocale
}
function getPreferredLanguage() {
  let localValue = getLocaleCodeFromUrl() || ''
  localValue = localValue?.toLowerCase()?.split('_')

  let preferredDisplayLanguage

  switch (localValue?.[0]) {
    case 'en':
      preferredDisplayLanguage = 'English'
      break
    case 'es':
      preferredDisplayLanguage = 'Spanish'
      break
    case 'zh':
      preferredDisplayLanguage = 'Chinese'
      break
    case 'fr':
      preferredDisplayLanguage = 'French'
      break
    case 'vi':
      preferredDisplayLanguage = 'Vietnamese'
      break
    default:
      preferredDisplayLanguage = 'English'
      break
  }

  return preferredDisplayLanguage
}

export {
  convertLocaleCode,
  displayLastNameFirst,
  getLocaleCodeFromUrl,
  isLATAM,
  isPAC,
  isEurope,
  getUrlWithoutLocale,
  getPreferredLanguage,
}
